import { useEffect, useMemo } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  appLanguageSelector,
  createRydeColumnHelper,
  fixedColumnNamesAF,
  gridControlsSF,
  IExtendableDto,
} from "@ryde/shared";

import { getFixedColumnsNamesFromTableDef } from "./helpers";
import { useFixedColumnsDynamic } from "./table-def.columns.fixed";
import { useDynamicTableDefColumns } from "./use-table-def.dynamic-columns";

const genericColumnHelper = createRydeColumnHelper<IExtendableDto>();

export const useTableColumnsDynamic = () => {
  const { entity } = usePageContext();
  const gridControls = useRecoilValue(gridControlsSF(entity));
  const language = useRecoilValue(appLanguageSelector);

  const { dynamicColumns } = useDynamicTableDefColumns({
    genericColumnHelper,
    gridControls,
  });
  const { fixedColumns } = useFixedColumnsDynamic(genericColumnHelper);
  const setFixedColumnNames = useSetRecoilState(fixedColumnNamesAF(entity));

  const columnKeysSerialized = useMemo(() => {
    return [...(dynamicColumns || []), ...(fixedColumns || [])]
      .map(x => x.id)
      .join(".");
  }, [dynamicColumns, fixedColumns]);

  const tableDefsGeneric = useMemo(() => {
    if (!dynamicColumns.length) {
      return [];
    }

    return [...fixedColumns, ...dynamicColumns];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnKeysSerialized, language]); // serialized to avoid re-creation because of different array ref

  const fixedColumnNames = useMemo(() => {
    return getFixedColumnsNamesFromTableDef(tableDefsGeneric);
  }, [tableDefsGeneric]);

  useEffect(() => {
    setFixedColumnNames(fixedColumnNames);
  }, [fixedColumnNames, setFixedColumnNames]);

  return { tableDefsGeneric };
};
